import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { schools } from '@/lib/api';
import { getStoredUser } from '@/lib/auth';
import { ArrowLeft, Clock, Loader2, Copy, Save, Building2, X, Plus, Archive, AlertTriangle, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';
import { usePlanLimits } from '@/hooks/usePlanLimits';

export default function SchoolSettings() {
  const { refreshStatus } = usePlanLimits();
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [user] = useState(getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [school, setSchool] = useState(null);
  const [activeSchoolsCount, setActiveSchoolsCount] = useState(null);
  const [newGradeLevel, setNewGradeLevel] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    start_time: '08:00',
    end_time: '15:00',
    contact_person: '',
    contact_position: '',
    contact_email: '',
    contact_phone: '',
    grade_levels: [],
    sub_teacher_categories: []
  });
  
  useEffect(() => {
    const fetchSchool = async () => {
      try {
        const response = await schools.get(schoolId);
        setSchool(response.data);
        setFormData({
          name: response.data.name,
          address: response.data.address,
          start_time: response.data.start_time,
          end_time: response.data.end_time || '15:00',
          contact_person: response.data.contact_person,
          contact_position: response.data.contact_position || '',
          contact_email: response.data.contact_email || '',
          contact_phone: response.data.contact_phone || '',
          grade_levels: response.data.grade_levels || [],
          sub_teacher_categories: response.data.sub_teacher_categories || []
        });
        // Load the count of active (non-archived) schools to enforce the
        // "at least one active school" rule on the archive action.
        try {
          const listRes = await schools.list();
          setActiveSchoolsCount(Array.isArray(listRes.data) ? listRes.data.length : null);
        } catch (_e) {
          setActiveSchoolsCount(null);
        }
      } catch (error) {
        toast.error('School not found');
        navigate('/employer');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSchool();
  }, [schoolId, navigate]);
  
  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  
  const handleAddGradeLevel = () => {
    if (!newGradeLevel.trim()) return;
    if (formData.grade_levels.includes(newGradeLevel.trim())) {
      toast.error('This category already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      grade_levels: [...prev.grade_levels, newGradeLevel.trim()]
    }));
    setNewGradeLevel('');
  };
  
  const handleRemoveGradeLevel = (level) => {
    setFormData(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.filter(l => l !== level)
    }));
  };

  const SUB_CATEGORY_LIMIT = 16;

  const handleAddSubCategory = () => {
    if (!newSubCategory.trim()) return;
    if (formData.sub_teacher_categories.length >= SUB_CATEGORY_LIMIT) {
      toast.error(`Maximum limit of ${SUB_CATEGORY_LIMIT} Sub Teacher Categories reached for this school.`);
      return;
    }
    if (formData.sub_teacher_categories.includes(newSubCategory.trim())) {
      toast.error('This Sub Teacher Category already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      sub_teacher_categories: [...prev.sub_teacher_categories, newSubCategory.trim()]
    }));
    setNewSubCategory('');
  };

  const handleRemoveSubCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      sub_teacher_categories: prev.sub_teacher_categories.filter(c => c !== cat)
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      await schools.update(schoolId, formData);
      toast.success('School settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCopyCode = () => {
    if (school?.school_code) {
      navigator.clipboard.writeText(school.school_code);
      toast.success('School code copied!');
    }
  };
  
  const handleArchiveSchool = async () => {
    setIsArchiving(true);
    try {
      await schools.archiveSchool(schoolId);
      refreshStatus(); // active-school count dropped — keep isAtLimit('schools') in sync
      toast.success('School archived');
      setShowArchiveConfirm(false);
      navigate('/employer');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to archive school');
      setShowArchiveConfirm(false);
    } finally {
      setIsArchiving(false);
    }
  };
  
  // The remaining active school cannot be archived; an admin must always keep
  // at least one active school. (activeSchoolsCount reflects non-archived schools.)
  const isLastActiveSchool = activeSchoolsCount !== null && activeSchoolsCount <= 1;
  // Only the school owner (not co-admins) may archive or manage co-admins.
  const isOwner = school?.employer_id === user?.user_id;
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/employer">
            <Button variant="ghost" size="icon" className="text-white hover:bg-slate-800">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <BrandLogo variant="white" size="lg" />
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            School Settings
          </h1>
          <p className="text-slate-600 mt-1">
            Manage default values for {school?.name}
          </p>
        </div>
        
        <div className="space-y-6">
          {/* School Code Card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">School Code</CardTitle>
              <CardDescription>
                Share this code with substitute teachers to connect them to your school
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-slate-100 rounded-lg px-4 py-3">
                  <span className="font-mono text-2xl font-bold text-slate-900">
                    {school?.school_code}
                  </span>
                </div>
                <Button 
                  variant="outline"
                  onClick={handleCopyCode}
                  data-testid="copy-code-btn"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Default Settings Form */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Default Information</CardTitle>
              <CardDescription>
                These values will be pre-filled when you create new positions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">School Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="settings-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    data-testid="settings-address-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="start_time">Default Start Time</Label>
                  <Input
                    id="start_time"
                    name="start_time"
                    type="time"
                    value={formData.start_time}
                    onChange={handleChange}
                    required
                    data-testid="settings-start-time-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end_time">Default End Time</Label>
                  <Input
                    id="end_time"
                    name="end_time"
                    type="time"
                    value={formData.end_time}
                    onChange={handleChange}
                    required
                    data-testid="settings-end-time-input"
                  />
                  <p className="text-xs text-slate-500">
                    Start and end times will be pre-filled when you create new positions
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleChange}
                    required
                    data-testid="settings-contact-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_position">Contact Position/Title</Label>
                  <Input
                    id="contact_position"
                    name="contact_position"
                    value={formData.contact_position}
                    onChange={handleChange}
                    placeholder="e.g., Principal, Office Manager"
                    data-testid="settings-contact-position-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="contact@school.edu"
                    data-testid="settings-contact-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="settings-contact-phone-input"
                  />
                  <p className="text-xs text-slate-500">
                    Contact information is visible to approved Subs
                  </p>
                </div>
                
                {/* Position Categories Section */}
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <Label>Position Categories</Label>
                  <p className="text-xs text-slate-500 mb-2">
                    Define the categories of positions available at your school. These will appear in the position posting form.
                  </p>
                  
                  <div className="flex gap-2">
                    <Input
                      value={newGradeLevel}
                      onChange={(e) => setNewGradeLevel(e.target.value)}
                      placeholder="e.g., Kindergarten, Grade 1, Grade 2"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddGradeLevel())}
                      data-testid="new-grade-level-input"
                    />
                    <Button 
                      type="button"
                      onClick={handleAddGradeLevel}
                      variant="outline"
                      data-testid="add-grade-level-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {formData.grade_levels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.grade_levels.map((level) => (
                        <Badge 
                          key={level} 
                          variant="secondary"
                          className="flex items-center gap-1 px-3 py-1"
                        >
                          {level}
                          <button
                            type="button"
                            onClick={() => handleRemoveGradeLevel(level)}
                            className="ml-1 hover:text-red-600"
                            data-testid={`remove-grade-${level}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {formData.grade_levels.length === 0 && (
                    <p className="text-sm text-slate-400 italic">
                      No position categories defined. Default options will be shown when posting positions.
                    </p>
                  )}
                </div>

                {/* Sub Teacher Categories Section */}
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <Label>Sub Teacher Categories</Label>
                    <span className="text-xs text-slate-400" data-testid="sub-category-count">
                      {formData.sub_teacher_categories.length}/{SUB_CATEGORY_LIMIT}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Create categories of teacher competencies to help you classify your Subs and choose the right person for each job (max {SUB_CATEGORY_LIMIT}).
                  </p>

                  <div className="flex gap-2">
                    <Input
                      value={newSubCategory}
                      onChange={(e) => setNewSubCategory(e.target.value)}
                      placeholder="e.g., Math, Science, Special Education"
                      disabled={formData.sub_teacher_categories.length >= SUB_CATEGORY_LIMIT}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSubCategory())}
                      data-testid="new-sub-category-input"
                    />
                    <Button
                      type="button"
                      onClick={handleAddSubCategory}
                      variant="outline"
                      disabled={formData.sub_teacher_categories.length >= SUB_CATEGORY_LIMIT}
                      data-testid="add-sub-category-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.sub_teacher_categories.length >= SUB_CATEGORY_LIMIT && (
                    <p className="text-sm text-amber-600 mt-1" data-testid="sub-category-limit-msg">
                      Maximum limit of {SUB_CATEGORY_LIMIT} Sub Teacher Categories reached for this school.
                    </p>
                  )}

                  {formData.sub_teacher_categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.sub_teacher_categories.map((cat) => (
                        <Badge
                          key={cat}
                          variant="secondary"
                          className="flex items-center gap-1 px-3 py-1"
                        >
                          {cat}
                          <button
                            type="button"
                            onClick={() => handleRemoveSubCategory(cat)}
                            className="ml-1 hover:text-red-600"
                            data-testid={`remove-sub-category-${cat}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  {formData.sub_teacher_categories.length === 0 && (
                    <p className="text-sm text-slate-400 italic">
                      No Sub Teacher Categories defined yet.
                    </p>
                  )}
                </div>
                
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="bg-slate-900 hover:bg-slate-800"
                    disabled={isSaving}
                    data-testid="save-settings-btn"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
          {/* Co-Admins (owner only) */}
          {isOwner && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-600" />
                  Co-Admins
                </CardTitle>
                <CardDescription>
                  Invite trusted colleagues to co-manage this school. Co-admins can post positions, manage subs, and view analytics, but cannot change billing or manage other co-admins.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  variant="outline"
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                  onClick={() => navigate(`/employer/school/${schoolId}/co-admins`)}
                  data-testid="manage-co-admins-btn"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Manage Co-Admins
                </Button>
              </CardContent>
            </Card>
          )}
          {/* Archive School */}
          {isOwner && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Archive className="h-5 w-5 text-amber-600" />
                Archive School
              </CardTitle>
              <CardDescription>
                Archiving hides this school from your main dashboard. You can restore it later from Archived Schools.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        variant="outline"
                        className="text-amber-700 border-amber-300 hover:bg-amber-100"
                        onClick={() => {
                          // Never open the confirmation modal when disabled.
                          if (isLastActiveSchool) return;
                          setShowArchiveConfirm(true);
                        }}
                        disabled={isLastActiveSchool}
                        data-testid="archive-school-btn"
                      >
                        <Archive className="h-4 w-4 mr-2" />
                        Archive This School
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isLastActiveSchool && (
                    <TooltipContent>
                      <p>You must have at least one active school on your account.</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </CardContent>
          </Card>
          )}
        </div>
        
        {/* Archive Confirmation Dialog */}
        <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                Archive {school?.name}?
              </DialogTitle>
              <DialogDescription>
                This school will be hidden from your main dashboard. Existing positions and sub connections will remain intact. You can restore it at any time from the Archived Schools section.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowArchiveConfirm(false)}>
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700"
                onClick={handleArchiveSchool}
                disabled={isArchiving}
                data-testid="confirm-archive-school-btn"
              >
                {isArchiving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Archive className="h-4 w-4 mr-1" />}
                Archive School
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <AppFooter role="employer" />
    </div>
  );
}
